import { t } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { POST_MAX_FILE_COUNT, POST_MAX_FILE_SIZE } from "@/constants";

interface UseFileAttachmentProps {
  maxFileSize?: number;
  maxFileCount?: number;
  allowedFileTypes?: string[];
  onFilesChange?: (files: File[]) => void;
  initialFiles?: File[];
  initialFilesKey?: string;
}

interface UseFileAttachmentReturn {
  // State
  attachedFiles: File[];
  fileError: string | null;
  isFileSelectionInProgress: boolean;
  totalAttachmentSize: number;

  // Handlers
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeFile: (index: number) => void;
  handleAttachClick: () => void;
  clearFiles: () => void;
  addFiles: (files: File[]) => void;

  // Refs
  fileInputRef: React.RefObject<HTMLInputElement | null>;

  // Utils
  validateFile: (file: File) => boolean;
}

export const useFileAttachment = ({
  maxFileSize = POST_MAX_FILE_SIZE,
  maxFileCount = POST_MAX_FILE_COUNT,
  allowedFileTypes,
  onFilesChange,
  initialFiles,
  initialFilesKey,
}: UseFileAttachmentProps = {}): UseFileAttachmentReturn => {
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isFileSelectionInProgress, setIsFileSelectionInProgress] =
    useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const appliedInitialFilesKeyRef = useRef<string | null>(null);

  // Validate single file
  const validateFile = useCallback(
    (file: File): boolean => {
      // Check file size
      if (file.size > maxFileSize) {
        const errorMsg = `${t("common.fileSizeError")} max ${maxFileSize / 1024 / 1024}MB`;
        setFileError(errorMsg);
        return false;
      }

      // Check file type if restrictions are set
      if (allowedFileTypes && allowedFileTypes.length > 0) {
        const fileExtension = file.name.split(".").pop()?.toLowerCase();
        if (!fileExtension || !allowedFileTypes.includes(fileExtension)) {
          const errorMsg = `${t("common.fileExtensionError")} ${allowedFileTypes.join(", ")}`;
          setFileError(errorMsg);
          return false;
        }
      }

      setFileError(null);
      return true;
    },
    [maxFileSize, allowedFileTypes],
  );

  const appendFiles = useCallback(
    (filesToAdd: File[]) => {
      if (!filesToAdd.length) {
        return;
      }

      const availableSlots = Math.max(maxFileCount - attachedFiles.length, 0);
      if (availableSlots <= 0) {
        setFileError(t("common.fileCountError", { maxCount: maxFileCount }));
        return;
      }

      const validFiles = filesToAdd.filter(validateFile);
      if (validFiles.length === 0) {
        return;
      }

      if (validFiles.length > availableSlots) {
        setFileError(t("common.fileCountError", { maxCount: maxFileCount }));
      }

      const filesToAppend = validFiles.slice(0, availableSlots);

      setAttachedFiles((prev) => {
        const newFiles = [...prev, ...filesToAppend];
        onFilesChange?.(newFiles);
        return newFiles;
      });
    },
    [attachedFiles.length, maxFileCount, validateFile, onFilesChange],
  );

  useEffect(() => {
    if (!initialFilesKey || !initialFiles?.length) {
      return;
    }
    if (appliedInitialFilesKeyRef.current === initialFilesKey) {
      return;
    }
    if (attachedFiles.length > 0) {
      return;
    }

    const validFiles = initialFiles.filter(validateFile).slice(0, maxFileCount);
    if (validFiles.length === 0) {
      appliedInitialFilesKeyRef.current = initialFilesKey;
      return;
    }

    if (initialFiles.length > maxFileCount) {
      setFileError(t("common.fileCountError", { maxCount: maxFileCount }));
    }

    setAttachedFiles(validFiles);
    onFilesChange?.(validFiles);
    appliedInitialFilesKeyRef.current = initialFilesKey;
  }, [
    attachedFiles.length,
    initialFiles,
    initialFilesKey,
    maxFileCount,
    onFilesChange,
    validateFile,
  ]);

  // Handle file input change
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsFileSelectionInProgress(false);

      if (e.target.files && e.target.files.length > 0) {
        const filesArray = Array.from(e.target.files);
        appendFiles(filesArray);

        // Reset input value to allow selecting the same file again
        e.target.value = "";
      }
    },
    [appendFiles],
  );

  const addFiles = useCallback(
    (files: File[]) => {
      appendFiles(files);
    },
    [appendFiles],
  );

  // Remove file by index
  const removeFile = useCallback(
    (index: number) => {
      const newFiles = attachedFiles.filter((_, i) => i !== index);
      setAttachedFiles(newFiles);
      setFileError(null);
      onFilesChange?.(newFiles);
    },
    [attachedFiles, onFilesChange],
  );

  // Trigger file input click
  const handleAttachClick = useCallback(() => {
    setIsFileSelectionInProgress(true);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Clear all files
  const clearFiles = useCallback(() => {
    setAttachedFiles([]);
    setFileError(null);
    onFilesChange?.([]);
  }, [onFilesChange]);

  const totalAttachmentSize = useMemo(
    () => attachedFiles.reduce((total, current) => total + current.size, 0),
    [attachedFiles],
  );

  return {
    // State
    attachedFiles,
    fileError,
    isFileSelectionInProgress,
    totalAttachmentSize,

    // Handlers
    handleFileChange,
    removeFile,
    handleAttachClick,
    clearFiles,
    addFiles,

    // Refs
    fileInputRef,

    // Utils
    validateFile,
  };
};
